import { useEffect, useRef } from "react"
import { hasModifierKey } from "@kit/design/utils/platform"

// How long a chord stays "armed" waiting for the next key (e.g. after "g").
const CHORD_TIMEOUT_MS = 1000

const isEditableTarget = target => {
  if (!target) return false
  const tag = target.tagName
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  )
}

/**
 * Global keyboard handling for the command palette:
 *   - Cmd/Ctrl+K toggles the palette open.
 *   - Chords like "g s" run their `onTrigger` against `api` (navigate, open a
 *     subpage, etc.). Ignored while typing, while a modifier is held, or while
 *     the palette is open.
 *
 * @param {object} args
 * @param {boolean} args.open - whether the palette is currently open
 * @param {() => void} args.toggleOpen - opens when closed, closes (with reset) when open
 * @param {import("./types").Shortcut[]} args.shortcuts - visible chords
 * @param {import("./types").PaletteApi} args.api - passed to a matched chord's onTrigger
 */
export function useCommandPaletteHotkeys({ open, toggleOpen, shortcuts, api }) {
  // Refs so the single listener always sees current values without re-binding.
  const openRef = useRef(open)
  const toggleOpenRef = useRef(toggleOpen)
  const shortcutsRef = useRef(shortcuts)
  const apiRef = useRef(api)
  openRef.current = open
  toggleOpenRef.current = toggleOpen
  shortcutsRef.current = shortcuts
  apiRef.current = api

  useEffect(() => {
    let pending = []
    let timer = null

    const resetChord = () => {
      pending = []
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
    }

    const handleKeyDown = event => {
      // Cmd/Ctrl+K toggles the palette from anywhere.
      if (hasModifierKey(event) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        toggleOpenRef.current()
        return
      }

      // Chords: only when idle — palette closed, not typing, no modifiers.
      if (
        openRef.current ||
        isEditableTarget(event.target) ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return
      }

      const key = event.key.toLowerCase()
      if (key.length !== 1) return

      pending.push(key)
      const sequence = pending.join(" ")
      const chords = shortcutsRef.current

      const exact = chords.find(chord => chord.shortcut === sequence)
      if (exact) {
        resetChord()
        exact.onTrigger(apiRef.current)
        return
      }

      const isPrefix = chords.some(chord =>
        chord.shortcut.startsWith(`${sequence} `)
      )
      if (!isPrefix) {
        // Not a chord we know — start over from this key.
        resetChord()
        pending = [key]
      }

      if (timer) clearTimeout(timer)
      timer = setTimeout(resetChord, CHORD_TIMEOUT_MS)
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      resetChord()
    }
  }, [])
}
